import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search, X, FileText, Library, Bot, LayoutGrid, Shield, ShieldCheck,
  BarChart3, ClipboardList, Users, Settings, BookOpen, GraduationCap,
  PlusCircle, Upload, Clock, CheckCircle, AlertCircle, Network,
  Vote, Calendar, Zap, Activity, CreditCard, MessageSquare,
  ChevronRight, Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  section: "actions" | "navigate" | "policies";
  action: () => void;
  badge?: string;
  badgeColor?: string;
}

interface PolicyResult {
  id: string;
  title: string;
  status: string;
  department: string | null;
}

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  approved: "text-emerald-600 bg-emerald-50",
  draft: "text-zinc-500 bg-zinc-100",
  under_review: "text-blue-600 bg-blue-50",
  pending_approval: "text-amber-600 bg-amber-50",
  rejected: "text-red-600 bg-red-50",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  draft: "Draft",
  under_review: "Under Review",
  pending_approval: "Pending",
  rejected: "Rejected",
};

export function CommandBar({ open, onClose }: CommandBarProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [policies, setPolicies] = useState<PolicyResult[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const go = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose]
  );

  const staticActions: CommandItem[] = [
    {
      id: "create-policy",
      label: "Create a Policy",
      description: "Start drafting a new policy from scratch or template",
      icon: PlusCircle,
      section: "actions",
      action: () => go("/begin"),
    },
    {
      id: "upload-policy",
      label: "Upload a Policy",
      description: "Import an existing PDF or Word document",
      icon: Upload,
      section: "actions",
      action: () => go("/upload-policy"),
    },
    {
      id: "ai-agent",
      label: "Open AI Agent",
      description: "Chat with Polybot to create and review policies",
      icon: Bot,
      section: "actions",
      action: () => go("/ai-agent"),
    },
    {
      id: "compliance-tasks",
      label: "View Compliance Tasks",
      description: "Check your outstanding compliance task list",
      icon: CheckCircle,
      section: "actions",
      action: () => go("/compliance/tasks"),
    },
    {
      id: "risk-register",
      label: "Open Risk Register",
      description: "View and manage all identified risks",
      icon: AlertCircle,
      section: "actions",
      action: () => go("/risk/register"),
    },
    {
      id: "templates",
      label: "Browse Templates",
      description: "Start from a pre-built policy template",
      icon: Library,
      section: "actions",
      action: () => go("/templates"),
    },
  ];

  const navItems: CommandItem[] = [
    { id: "nav-dashboard", label: "Dashboard", icon: LayoutGrid, section: "navigate", action: () => go("/dashboard") },
    { id: "nav-policy-library", label: "Policy Library", icon: Library, section: "navigate", action: () => go("/policy-library") },
    { id: "nav-compliance", label: "Compliance Dashboard", icon: Shield, section: "navigate", action: () => go("/compliance") },
    { id: "nav-risk", label: "Risk Overview", icon: ShieldCheck, section: "navigate", action: () => go("/risk/overview") },
    { id: "nav-risk-heatmap", label: "Risk Heat Map", icon: BarChart3, section: "navigate", action: () => go("/risk/heat-map") },
    { id: "nav-governance", label: "Governance Structure", icon: Network, section: "navigate", action: () => go("/governance/dashboard") },
    { id: "nav-tor", label: "Terms of Reference", icon: ClipboardList, section: "navigate", action: () => go("/governance/tor") },
    { id: "nav-voting", label: "Voting & Decisions", icon: Vote, section: "navigate", action: () => go("/governance/voting") },
    { id: "nav-meetings", label: "Meeting Minutes", icon: Calendar, section: "navigate", action: () => go("/governance/meetings") },
    { id: "nav-automation", label: "Task Automation", icon: Zap, section: "navigate", action: () => go("/governance/automation") },
    { id: "nav-kb", label: "Knowledge Base", icon: BookOpen, section: "navigate", action: () => go("/knowledge-base") },
    { id: "nav-quizzes", label: "Quizzes", icon: GraduationCap, section: "navigate", action: () => go("/quizzes") },
    { id: "nav-team", label: "Team Management", icon: Users, section: "navigate", action: () => go("/team") },
    { id: "nav-activity", label: "Audit Trail", icon: Activity, section: "navigate", action: () => go("/activity-log") },
    { id: "nav-settings", label: "Settings", icon: Settings, section: "navigate", action: () => go("/settings") },
    { id: "nav-billing", label: "Billing", icon: CreditCard, section: "navigate", action: () => go("/billing") },
    { id: "nav-feedback", label: "Feedback", icon: MessageSquare, section: "navigate", action: () => go("/feedback") },
  ];

  const policyItems: CommandItem[] = policies.map((p) => ({
    id: `policy-${p.id}`,
    label: p.title,
    description: p.department ?? undefined,
    icon: FileText,
    section: "policies" as const,
    action: () => go(`/policy/${p.id}`),
    badge: STATUS_LABELS[p.status] ?? p.status,
    badgeColor: STATUS_COLORS[p.status] ?? "text-zinc-500 bg-zinc-100",
  }));

  const q = query.trim().toLowerCase();

  const filteredActions = q
    ? staticActions.filter(
        (i) =>
          i.label.toLowerCase().includes(q) ||
          i.description?.toLowerCase().includes(q)
      )
    : staticActions;

  const filteredNav = q
    ? navItems.filter((i) => i.label.toLowerCase().includes(q))
    : navItems;

  const allItems: CommandItem[] = [
    ...(filteredActions.length ? filteredActions : []),
    ...(filteredNav.length ? filteredNav : []),
    ...policyItems,
  ];

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setPolicies([]);
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!profile?.company_id) return;
    clearTimeout(debounceRef.current);

    if (!q) {
      setPolicies([]);
      return;
    }

    setLoadingPolicies(true);
    debounceRef.current = setTimeout(async () => {
      const { data } = await supabase
        .from("policies")
        .select("id, title, status, department")
        .eq("company_id", profile.company_id!)
        .ilike("title", `%${q}%`)
        .limit(6);
      setPolicies((data as PolicyResult[]) ?? []);
      setLoadingPolicies(false);
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [q, profile?.company_id]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        allItems[activeIndex]?.action();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, allItems, activeIndex]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const sections = [
    { key: "actions", label: "Quick Actions", items: filteredActions },
    { key: "navigate", label: "Navigate", items: filteredNav },
    { key: "policies", label: "Policies", items: policyItems },
  ].filter((s) => s.items.length > 0);

  let globalIndex = 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 pointer-events-none">
            <motion.div
              key="panel"
              initial={{ opacity: 0, scale: 0.97, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -8 }}
              transition={{ duration: 0.15, ease: [0.2, 0, 0, 1] }}
              className="pointer-events-auto w-full max-w-xl bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Search input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search policies, actions, pages…"
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                />
                <div className="flex items-center gap-2">
                  {loadingPolicies && (
                    <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                  )}
                  {query ? (
                    <button
                      onClick={() => setQuery("")}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : (
                    <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                      esc
                    </kbd>
                  )}
                </div>
              </div>

              {/* Results */}
              <div
                ref={listRef}
                className="max-h-[60vh] overflow-y-auto overscroll-contain py-2"
              >
                {allItems.length === 0 && !loadingPolicies && (
                  <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No results for &ldquo;{query}&rdquo;
                  </p>
                )}

                {sections.map((section) => (
                  <div key={section.key}>
                    <p className="px-4 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {section.label}
                    </p>
                    {section.items.map((item) => {
                      const idx = globalIndex++;
                      const Icon = item.icon;
                      const isActive = idx === activeIndex;
                      return (
                        <button
                          key={item.id}
                          data-cmd-index={idx}
                          onClick={item.action}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                            isActive ? "bg-accent" : "hover:bg-accent/50"
                          )}
                        >
                          <div
                            className={cn(
                              "h-7 w-7 rounded-md flex items-center justify-center shrink-0",
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-foreground block truncate">
                              {item.label}
                            </span>
                            {item.description && (
                              <span className="text-xs text-muted-foreground block truncate">
                                {item.description}
                              </span>
                            )}
                          </div>
                          {item.badge && (
                            <span
                              className={cn(
                                "text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0",
                                item.badgeColor
                              )}
                            >
                              {item.badge}
                            </span>
                          )}
                          {isActive && (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-t border-border bg-muted/30">
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <kbd className="inline-flex items-center rounded border border-border px-1 py-0.5 text-[10px] font-medium">↑↓</kbd>
                  navigate
                </span>
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <kbd className="inline-flex items-center rounded border border-border px-1 py-0.5 text-[10px] font-medium">↵</kbd>
                  open
                </span>
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <kbd className="inline-flex items-center rounded border border-border px-1 py-0.5 text-[10px] font-medium">esc</kbd>
                  close
                </span>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
